import { db } from "firebaseConfig";
import { realEstateContactConverter } from "utils/firestoreConverters";
import { generateMetadataFromContact } from "utils/Haim/firestoreUtils";
import firebase from "firebase/compat";
import { latestBulkMessageTime } from "./lastBulkMessage";
import type { RealEstateContact } from "../.../../../@types/sharedSchema";

/**
 * Contacts whose `updatedTime` moved after `since` — the legacy book's live feed.
 *
 * `updatedTime` is stamped by a Cloud Function on every real write, a few seconds after it,
 * and on a NEW contact too — so this query sees creations, not only edits. It does NOT see a
 * delete, and a write that carries `skipUpdatedTimeAroundDate` (a backfill) does not bring a
 * contact INTO the window, which is what keeps a migration from flooding every open dashboard.
 *
 * The single consumer is `AppContext`; see `legacyBook.ts` for what it does with the result.
 */
export function getUpdatedContactsQuery(
  email: string,
  since: Date,
  limit: number
): firebase.firestore.Query<RealEstateContact> {
  return db
    .collection(`/dashboard-users-v2/${email}/contacts`)
    .where("updatedTime", ">", since)
    .orderBy("updatedTime", "desc")
    .withConverter(realEstateContactConverter)
    .limit(limit);
}

/**
 * Post-processes a raw contact from Firestore into a fully populated RealEstateContact.
 * Sets the `_metadata` block, the `contactOriginAdditionalInfo` fallback, and coerces a
 * string budget to a number.
 *
 * ── IT NO LONGER STAMPS `callerPhoneNumber`, AND THAT IS THE POINT ────────────────
 *
 * It used to open with `data.callerPhoneNumber = data.id`, which quietly made the app
 * correct on every path that called this function and quietly broken on every path that
 * did not. The stored field is a legacy mirror: measured 2026-09-01 it is present on 267
 * of 2,683 contact documents (60 accounts) — and of those 267, twelve DISAGREE with the
 * document id (`0503045800` for `+972503045800`, `+33 6 16 79 08 94` with spaces and RTL
 * marks). So the line was not a convenience, it was a mask.
 *
 * What it masked: `ContactDetailView` resolved a contact as `liveContact ?? cachedContact`
 * — the raw Firestore snapshot WON over the normalised copy from `allContacts` (the page
 * renders only the live snapshot now) — so the whole contact page and every dialog it
 * opens saw the unstamped document. The edit
 * dialog's phone box was empty there and filled everywhere else, which is why it survived
 * for years without a report.
 *
 * THE PHONE IS `contact.id` (the document id, which cannot be absent) or `contact.contactId`
 * (the Cloud-Function-stamped copy, for objects that are not documents — CRM rows, server
 * payloads). Do not reintroduce the stamp: the next surface to skip this function should
 * fail loudly at review time, not silently in production.
 */
export function postProcessContact(
  data: RealEstateContact,
  userEmail: string
): RealEstateContact {
  // The LATER of the two rails' stamps — never the extension's alone, which threw away a
  // campaign's send (`lastBulkMessageReceivedTime` is where the server stamps one). See
  // `latestBulkMessageTime`.
  data.lastBulkMessageReceivedTime = latestBulkMessageTime(data);
  data.contactOriginAdditionalInfo = data.contactOriginAdditionalInfo ?? userEmail;
  data._metadata = data._metadata ?? generateMetadataFromContact(userEmail, data.id);

  const customerBudget = data.customerBudget;
  if (typeof customerBudget === "string") {
    data.customerBudget = parseCustomerBudget(customerBudget) as number;
  }

  const aiCustomerDetailsMetadata = data.AICustomerDetailsMetadata?.customerBudget;
  if (typeof aiCustomerDetailsMetadata === "string") {
    data.AICustomerDetailsMetadata.customerBudget = parseCustomerBudget(aiCustomerDetailsMetadata) as number;
  }

  return data;
}

export function parseCustomerBudget(
  customerBudget: number | string | undefined
): number | string | undefined {
  if (customerBudget === undefined) return;

  if (typeof customerBudget === "string") {
    const parsedCustomerBudget = parseInt(customerBudget.replace(/,/g, ""));

    if (!isNaN(parsedCustomerBudget)) return parsedCustomerBudget;
  }

  return customerBudget;
}
